import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActualServer, BudgetAllocationItem, BudgetAllocationSuggestion } from '../../types';
import {
  applyBudgetAllocation,
  repromptBudgetAllocation,
  suggestBudgetAllocation
} from '../../utils/budgetApi';
import { formatBudgetCurrency } from '../../utils/budgetFormat';
import { isRateLimitError } from '../../utils/overspendApi';

interface BudgetAllocationModalProps {
  isVisible: boolean;
  onClose: () => void;
  onApplied: () => void;
  server: ActualServer;
  month: string;
  currency?: string;
  toBudget: number;
}

const BudgetAllocationModal: React.FC<BudgetAllocationModalProps> = ({
  isVisible,
  onClose,
  onApplied,
  server,
  month,
  currency = 'USD',
  toBudget
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reprompting, setReprompting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<BudgetAllocationSuggestion | null>(null);
  const [userFeedback, setUserFeedback] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setLoading(false);
      setApplying(false);
      setReprompting(false);
      setError(null);
      setSuggestion(null);
      setUserFeedback('');
      setSuccess(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await suggestBudgetAllocation(server, month);
        setSuggestion(result);
      } catch (err) {
        if (err instanceof Error && (err as Error & { code?: string }).code === 'AI_UNAVAILABLE') {
          setError(t('allocation.aiUnavailable', { ns: 'budget' }));
        } else if (isRateLimitError(err)) {
          setError(t('allocation.rateLimitExceeded', { ns: 'budget' }));
        } else {
          setError(err instanceof Error ? err.message : t('allocation.errorGeneric', { ns: 'budget' }));
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isVisible, server, month, t]);

  if (!isVisible) return null;

  const formatAmount = (amountCents: number) => formatBudgetCurrency(amountCents, currency);

  const handleReprompt = async () => {
    if (!suggestion || !userFeedback.trim() || suggestion.source !== 'ai') return;
    setReprompting(true);
    setError(null);
    try {
      const updated = await repromptBudgetAllocation(
        server,
        month,
        userFeedback.trim(),
        suggestion.allocations
      );
      setSuggestion(updated);
      setUserFeedback('');
    } catch (err) {
      setError(isRateLimitError(err)
        ? t('allocation.rateLimitExceeded', { ns: 'budget' })
        : err instanceof Error ? err.message : t('allocation.errorGeneric', { ns: 'budget' }));
    } finally {
      setReprompting(false);
    }
  };

  const handleApply = async () => {
    if (!suggestion || suggestion.source === 'none') return;
    setApplying(true);
    setError(null);
    try {
      await applyBudgetAllocation(server, month, suggestion.source, suggestion.allocations);
      setSuccess(true);
      onApplied();
    } catch (err) {
      setError(isRateLimitError(err)
        ? t('allocation.rateLimitExceeded', { ns: 'budget' })
        : err instanceof Error ? err.message : t('allocation.errorGeneric', { ns: 'budget' }));
    } finally {
      setApplying(false);
    }
  };

  const sourceLabel = suggestion?.source === 'goals'
    ? t('allocation.fromGoals', { ns: 'budget' })
    : suggestion?.source === 'ai'
      ? t('allocation.fromAi', { ns: 'budget' })
      : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content overspend-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('allocation.title', { ns: 'budget' })}</h3>
          <button className="modal-close" onClick={onClose} type="button">×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>{t('allocation.loading', { ns: 'budget' })}</p>
            </div>
          )}

          {!loading && error && !suggestion && (
            <div className="error">
              <h4>{t('allocation.errorTitle', { ns: 'budget' })}</h4>
              <p>{error}</p>
              <div className="step-actions">
                <button type="button" className="cancel-button" onClick={onClose}>
                  {t('allocation.close', { ns: 'budget' })}
                </button>
              </div>
            </div>
          )}

          {!loading && suggestion && !success && (
            <>
              <p className="overspend-intro">{t('allocation.intro', { ns: 'budget' })}</p>
              <p className="overspend-summary">
                {t('allocation.available', { ns: 'budget', amount: formatAmount(toBudget) })}
                {suggestion.totalAllocated != null && (
                  <>
                    {' · '}
                    {t('allocation.toAllocate', {
                      ns: 'budget',
                      amount: formatAmount(suggestion.totalAllocated)
                    })}
                  </>
                )}
              </p>
              {sourceLabel && <p className="overspend-suggestion-type">{sourceLabel}</p>}
              {suggestion.explanation && (
                <p className="overspend-explanation">{suggestion.explanation}</p>
              )}

              {suggestion.source === 'ai' && (
                <div className="overspend-feedback">
                  <label htmlFor="allocation-feedback-input">
                    {t('allocation.feedbackLabel', { ns: 'budget' })}
                  </label>
                  <textarea
                    id="allocation-feedback-input"
                    className="overspend-feedback-input"
                    value={userFeedback}
                    onChange={event => setUserFeedback(event.target.value)}
                    placeholder={t('allocation.feedbackPlaceholder', { ns: 'budget' })}
                    rows={3}
                    disabled={reprompting}
                  />
                  <button
                    type="button"
                    className="secondary"
                    onClick={handleReprompt}
                    disabled={reprompting || !userFeedback.trim()}
                  >
                    {reprompting
                      ? t('allocation.reprompting', { ns: 'budget' })
                      : t('allocation.reprompt', { ns: 'budget' })}
                  </button>
                </div>
              )}

              <div className="overspend-coverage-list">
                {suggestion.allocations.length === 0 ? (
                  <p>{t('allocation.noAllocations', { ns: 'budget' })}</p>
                ) : (
                  suggestion.allocations.map((item: BudgetAllocationItem) => (
                    <section key={item.categoryId} className="overspend-coverage-card">
                      <header>
                        <strong>{item.categoryName}</strong>
                        <span className="overspend-amount-badge">
                          {t('allocation.allocateAmount', {
                            ns: 'budget',
                            amount: formatAmount(item.amount)
                          })}
                        </span>
                      </header>
                      {item.newBudgeted != null && (
                        <p className="overspend-explanation">
                          {t('allocation.newBudgeted', {
                            ns: 'budget',
                            amount: formatAmount(item.newBudgeted)
                          })}
                        </p>
                      )}
                      {item.reason && (
                        <small className="transfer-reason">{item.reason}</small>
                      )}
                    </section>
                  ))
                )}
              </div>

              {error && (
                <div className="error">
                  <p>{error}</p>
                </div>
              )}

              <div className="step-actions">
                <button type="button" className="cancel-button" onClick={onClose}>
                  {t('allocation.skip', { ns: 'budget' })}
                </button>
                <button
                  type="button"
                  className="import-execute-button"
                  onClick={handleApply}
                  disabled={applying || suggestion.allocations.length === 0}
                >
                  {applying
                    ? t('allocation.applying', { ns: 'budget' })
                    : t('allocation.confirm', { ns: 'budget' })}
                </button>
              </div>
            </>
          )}

          {success && (
            <div className="import-result">
              <h5>{t('allocation.successTitle', { ns: 'budget' })}</h5>
              <p>{t('allocation.successMessage', { ns: 'budget' })}</p>
              <div className="step-actions">
                <button type="button" className="close-modal-button secondary" onClick={onClose}>
                  {t('allocation.close', { ns: 'budget' })}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetAllocationModal;
